import * as React from "react";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import type { Abbreviation } from "./NarrativeTextArea";

export type NarrativeAbbreviationsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  abbreviations: Abbreviation[];
};

export function NarrativeAbbreviationsDialog({
  open,
  onOpenChange,
  abbreviations,
}: NarrativeAbbreviationsDialogProps) {
  const [query, setQuery] = React.useState("");

  // Reset search when dialog opens
  React.useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return abbreviations ?? [];
    const q = query.toLowerCase();
    return (abbreviations ?? []).filter(
      (a) =>
        (a.trigger ?? '').toLowerCase().includes(q) ||
        (a.replacement ?? '').toLowerCase().includes(q),
    );
  }, [abbreviations, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Escape") e.stopPropagation(); }}
      >
        <DialogHeader>
          <DialogTitle>Narrative abbreviations</DialogTitle>
          <DialogDescription>
            Type an abbreviation followed by space to auto-expand it in the narrative.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search abbreviations..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="overflow-y-auto rounded-md border">
          {filtered.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No abbreviations found.
            </p>
          ) : (
            <div className="grid grid-cols-[auto_1fr] items-start">
              {filtered.map((abbr, i) => (
                <React.Fragment key={i}>
                  <div className={`flex items-center justify-end gap-1.5 px-3 py-2 ${i % 2 === 1 ? "bg-muted/30" : ""}`}>
                    <kbd className="inline-flex h-6 items-center rounded border border-border bg-muted px-2 font-mono text-xs font-medium text-muted-foreground">
                      {abbr.trigger}
                    </kbd>
                  </div>
                  <div className={`px-3 py-2 text-sm text-muted-foreground ${i % 2 === 1 ? "bg-muted/30" : ""}`}>
                    {abbr.replacement}
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Manage your abbreviations in{" "}
          <Link
            to="/settings/abbreviations"
            target="_blank"
            className="underline hover:text-foreground"
          >
            Settings
          </Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
